import React, { lazy, useState, useEffect, useMemo, useContext } from "react";
import { FormGroup, onMount, withPopover, SubmitButton } from "components/common";
import { track, Fieldset, actions, Control, LocalForm } from "react-redux-form";
import EmployeeContext from "contexts/EmployeeContext";
import { FormGroup as FormGroup_ } from "components/common/Forms";

import { Row, Col, ModalBody, ModalHeader, Table, Button } from "reactstrap";
import { Trans, useTranslation, withTranslation } from "react-i18next";
import moment from "moment";
import { Link } from "react-router-dom";
import { compose, withStateHandlers, withPropsOnChange, withHandlers } from "recompose";
import classNames from "classnames";
import { keyBy, chain, capitalize, isUndefined, transform, forEach, keys, find, sumBy, includes, compact, get, range, set, startsWith } from "lodash";
import { formatNumber, formatCurrency, CurrencySymbol } from "services/util";
import diff from "deep-diff";
import shortid from "shortid";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import useModal from "../hooks/useModal";
import { useXeroClient } from "../hooks";
import { useQuery } from "react-query";
import "../assets/scss/viewStyle.css"
import { formatDate } from "services/util";
import { NotificationManager } from "react-notifications";


const recalculate = (
  prevItem,
  {
    taxRates,
    dispatch,
    organisation,
    includeDiscount,
    isReceivable,
    isPayable,
    indexedInventory,
  }
) =>
  dispatch &&
  dispatch((_, getState) => {
    let local = getState().local;

    if (!local.LineAmountTypes) {
      dispatch(actions.change("local.LineAmountTypes", "Exclusive"));
    }
    if (!local.CurrencyCode) {
      dispatch(actions.change("local.CurrencyCode", organisation.BaseCurrency));
    }
    if (!local.LineItems.length && local.Contact) {
      const customerDiscount = get(local, "Contact.Discount") || undefined;
      const customerTaxType =
        get(
          local,
          isReceivable
            ? "Contact.AccountsReceivableTaxType"
            : "Contact.AccountsPayableTaxType"
        ) || undefined;
      const customerAccountCode =
        get(
          local,
          isReceivable
            ? "Contact.SalesDefaultAccountCode"
            : isPayable
              ? "Contact.PurchasesDefaultAccountCode"
              : null
        ) || undefined;

      dispatch(
        actions.push("local.LineItems", {
          _id: shortid(),
          DiscountRate: includeDiscount ? customerDiscount : undefined,
          TaxType: customerTaxType,
          AccountCode: customerAccountCode,
        })
      );
    }
    local = getState().local;

    let changed = null;

    if (prevItem) {
      const itemDiff = diff(prevItem, local);
      if (!itemDiff) return;

      changed = transform(
        itemDiff,
        (result, { path, kind }) => {
          if (
            path[0] === "LineAmountTypes" ||
            (path[0] === "LineItems" && kind === "A")
          ) {
            result.recalculateAllLines = true;
          } else if (path[0] === "LineItems" && kind !== "A") {
            switch (path[2]) {
              case "AccountCode":
                break;
              case "DiscountRate":
              case "UnitAmount":
              case "Quantity":
              case "TaxAmount":
              case "TaxType":
                result.linesChanged.push(path[1]);
                break;
              default:
                break;
            }
          }
        },
        {
          linesChanged: [],
        }
      );
    } else {
      changed = {
        recalculateAllLines: true,
      };
    }

    if (changed.recalculateAllLines) {
      changed.linesChanged = keys(local.LineItems);
    }
    forEach(changed.linesChanged, (idx) => {
      const line = local.LineItems[idx];
      const discountRate = includeDiscount
        ? parseFloat(line.DiscountRate) / 100 || 0
        : 0;
      const unitAmount = parseFloat(line.UnitAmount) || 0;
      const quantity = parseFloat(line.Quantity) || 0;
      const tax_amount = parseFloat(line.TaxAmount) || 0;
      let lineAmount = unitAmount * quantity;
      const discountAmount = lineAmount * discountRate;
      lineAmount -= discountAmount;

      const taxRate = find(taxRates, { TaxType: line.TaxType });
      let taxAmount = 0;

      if (local.LineAmountTypes === "NoTax") {
        dispatch(actions.omit(`local.LineItems[${idx}]`, ["TaxType"]));
      } else if (local.LineAmountTypes === "Exclusive") {
        taxAmount = tax_amount || (taxRate ? lineAmount * (taxRate.EffectiveRate / 100) : 0);
      } else if (local.LineAmountTypes === "Inclusive") {
        taxAmount = tax_amount || (taxRate ? lineAmount - lineAmount / (1 + taxRate.EffectiveRate / 100) : 0);
      }

      const item = indexedInventory[line.ItemCode] ?? {};
      const { IsTrackedAsInventory, InventoryAssetAccountCode } = item;

      if (IsTrackedAsInventory && InventoryAssetAccountCode && isPayable) {
        dispatch(
          actions.change(
            `local.LineItems[${idx}].AccountCode`,
            InventoryAssetAccountCode
          )
        );
        dispatch(
          actions.change(`local.LineItems[${idx}].AccountCodeLocked`, true)
        );
      } else {
        dispatch(
          actions.change(`local.LineItems[${idx}].AccountCodeLocked`, false)
        );
      }

      dispatch(
        actions.change(
          `local.LineItems[${idx}].TaxAmount`,
          formatNumber(taxAmount)
        )
      );
      dispatch(
        actions.change(
          `local.LineItems[${idx}].BaseTaxAmount`,
          formatNumber(taxAmount)
        )
      );
      dispatch(
        actions.change(
          `local.LineItems[${idx}].LineAmount`,
          formatNumber(lineAmount)
        )
      );

      dispatch(
        actions.change(
          `local.LineItems[${idx}].DiscountAmount`,
          includeDiscount ? formatNumber(discountAmount) : undefined
        )
      );
    });

    if (changed.linesChanged.length || changed.recalculateAllLines) {
      local = getState().local;
      const amountPaid = parseFloat(local.AmountPaid) || 0;
      const creditAmount = parseFloat(local.AmountCredited) || 0;

      const subTotal = sumBy(
        local.LineItems,
        (v) => { return parseFloat(v.LineAmount) || 0 }
      );
      const totalTax = sumBy(
        local.LineItems,
        (v) => { return parseFloat(v.TaxAmount) || 0 }
      );
      const baseTax = sumBy(
        local.LineItems,
        (v) => parseFloat(v.BaseTaxAmount) || 0
      );

      const totalDiscount = sumBy(
        local.LineItems,
        (v) => parseFloat(v.DiscountAmount) || 0
      );

      dispatch(actions.change("local.SubTotal", formatNumber(subTotal)));
      dispatch(actions.change("local.TotalTax", formatNumber(totalTax)));
      dispatch(
        actions.change("local.TotalDiscount", formatNumber(totalDiscount))
      );

      if (
        local.LineAmountTypes === "NoTax" ||
        local.LineAmountTypes === "Inclusive"
      ) {
        dispatch(actions.change("local.Total", formatNumber(subTotal)));
        if (amountPaid || creditAmount) {
          dispatch(
            actions.change(
              "local.AmountDue",
              formatNumber((subTotal - amountPaid - creditAmount).toFixed(2))
            )
          );
        }
      } else if (local.LineAmountTypes === "Exclusive") {
        dispatch(
          actions.change("local.Total", formatNumber(subTotal + totalTax))
        );
        dispatch(
          actions.change("local.Adjustments", formatNumber(baseTax - totalTax))
        );
        if (amountPaid || creditAmount) {
          dispatch(
            actions.change(
              "local.AmountDue",
              formatNumber((subTotal + totalTax - amountPaid - creditAmount).toFixed(2))
            )
          );
        }
      }
    }
  });

const TrackingSelect = ({
  lineItem,
  trackingCategory,
}) => {
  const { Tracking } = lineItem;

  const selectedTracking = useMemo(() => {
    return (
      Tracking?.reduce((acc, i) => {
        acc[i.Name] = i.Option;
        return acc;
      }, {}) ?? {}
    );
  }, [Tracking]);

  const selectedOption = selectedTracking[trackingCategory.Name] ?? null;

  const displayValue = selectedOption !== null ? selectedOption : '-';

  return (
    <span style={{ color: '#525F7F' }}>{displayValue}</span>
  );
};

// TODO: check delete functionality keeps a row in place, not a big deal
export const LineItems = ({
  item,
  dispatch,
  accounts,
  taxRates,
  hideAccountAndTax,
  organisation,
  paymentText = "Less Payment",
  inventory: inventoryProp,
  includeDiscount,
  includeItems = true,
  isPayable,
  isReceivable,
  invoiceType,
  prepayments,
  reload,
}) => {

  const total = item.AmountDue / item.CurrencyRate

  const differentCurrencyShow = () => {
    return organisation && organisation.BaseCurrency !== item.CurrencyCode && item.CurrencyRate;
  };

  const { Modal: PrepaymentModal, setIsOpen: setPrepaymentModalOpen, toggle: PrepaymentModalToggle, isOpen: PrepaymentModalOpen } = useModal();


  const xero = useXeroClient();
  const { t } = useTranslation();
  const { employee } = useContext(EmployeeContext);
  const { data: trackingCategories } = useQuery(
    [
      "trackingcategories",
      {
        params: {
          Status: "ACTIVE",
        },
      },
    ],
    ({ queryKey }) => xero.get(...queryKey).get("data"),
    {
      placeholderData: [],
    }
  );

  const { Total } = item;

  useEffect(() => {
    dispatch(
      actions.map("local.Allocations", (i) => {
        if (!i.IsNew) return i;

        let amount = Total;
        const amountDue = i.Invoice?.AmountDue ?? null;

        if (amountDue !== null) {
          amount = Math.min(Number(amountDue), Number(Total));
          amount = isNaN(amount) ? "" : amount.toFixed(2);
        }

        return {
          ...i,
          Amount: amount,
        };
      })
    );
  }, [Total]);

  const countWords = (text) => {
    const words = text.split(/\s+/);
    return words.length;
  };

  const applyPrepayments = async (values) => {
    values = Object.values(values || {});
    const filteredValues = values.filter(value => value.AppliedAmount > 0);

    for (const value of filteredValues) {
      try {
        await xero.post(`prepayments/${value?.PrepaymentID}/allocations`, {
          'AppliedAmount': value.AppliedAmount,
          'Invoice': { 'InvoiceID': item.InvoiceID },
        });
        NotificationManager.success(t('Prepayment applied successfully.'));
      } catch (error) {
        NotificationManager.error(t('Failed to apply prepayment.'));
      }
    }
    reload()
  }

const removeAllocation = async (allocation) => {
  const ID = item.PrepaymentID || item.CreditNoteID || item.OverpaymentID
  const type = item?.CreditNoteID? 'creditnotes' : item.OverpaymentID? 'overpayments' : 'prepayments'
  try {
    await xero.delete(`${type}/${ID}/allocations/${allocation.AllocationID}`);
    NotificationManager.success(t('Deleted Successfully'));
    reload()
  } catch (error) {
    NotificationManager.error(t('Failed to delete'));
  }
}


  return (
    <div>
      <Row>
        <Col>
          <FormGroup className="text-left" style={{ paddingLeft: "10px" }}>
            <p style={{ fontSize: "1.2em" }}><Trans>Currency</Trans> : <strong><CurrencySymbol currency={item.CurrencyCode} /></strong></p>
            {organisation &&
              organisation.BaseCurrency !== item.CurrencyCode &&
              item.CurrencyRate ? (
              <div className="d-flex align-items-center form-text text-muted">
                <span>
                  <p className="font-italic"><Trans>This document is in a different currency.</Trans></p>
                </span>
              </div>
            ) : null}
          </FormGroup>
        </Col>
        <Col>
          <FormGroup className="text-right">
            <p style={{ paddingRight: "10px", fontSize: "1.2em" }}><Trans>Amounts are</Trans> <strong><Trans>Tax {item.LineAmountTypes}</Trans></strong></p>
          </FormGroup>
        </Col>
      </Row>
      <div className="line-items mb-4">
        <Table className="table table-sm small line-items--table" responsive>
          <thead>
            <tr>
              <th
                className={classNames("text-nowrap text-left", {
                  "d-none": !includeItems,
                })}
              >
                <Trans>Item/Description</Trans>
              </th>
              <th className="text-nowrap">
                <Trans>Qty</Trans>
              </th>
              <th className="text-nowrap">
                <Trans>Unit Price</Trans>
              </th>
              {includeDiscount && invoiceType !== "Bill" && (
                <th className="text-nowrap">
                  <Trans>Disc %</Trans>
                </th>
              )}
              {!hideAccountAndTax &&
                <th className="text-nowrap">
                  <Trans>Tax</Trans>
                </th>
              }
              {trackingCategories.map((i) => (
                <th key={i.TrackingCategoryID}>{i.Name}</th>
              ))}
              <th className="text-nowrap text-right">
                <Trans>Amount</Trans>
                <CurrencySymbol currency={item.CurrencyCode} />
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {item.LineItems.map((i, idx) => {
              const tracker = track(".LineItems[]", { _id: i._id });
              const accountName = accounts.length > 0 ? accounts.reduce((acc, mainCategory) => (acc || mainCategory.options.find(option => option.Code === i.AccountCode)?.Name), null) || '-' : '-'

              return (
                <Fieldset component="tr" model={tracker} key={i._id}>
                  <td className="td-color text-left">
                    {i.ItemCode ? (
                      <FormGroup style={{ fontSize: "1.3em" }}>
                        {i.ItemCode}
                      </FormGroup>
                    ) : null}
                    <FormGroup style={{ width: 400 }}>
                      {i.Description}
                    </FormGroup>
                    <div className={`my-2 text-left ${i.AccountCode && countWords(accountName) > 5 ? '' : 'd-flex'}`} style={{ color: "#6F6F7B" }}>
                      {i.AccountCode && (
                        <div>
                          Account: <strong>{accountName}&nbsp; • &nbsp;</strong>
                        </div>
                      )}
                      {i.TaxType && (
                        <div>
                          {taxRates.find(rate => rate.TaxType === i.TaxType) ? (
                            <span>
                              {taxRates.find(rate => rate.TaxType === i.TaxType).Name}
                              {' '}
                              <strong>
                                ({taxRates.find(rate => rate.TaxType === i.TaxType).DisplayTaxRate}%)
                              </strong>
                            </span>
                          ) : 'N/A'}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="td-color">
                    {i.Quantity || "-"}
                  </td>
                  <td className="td-color">
                    {i.UnitAmount || "-"}
                  </td>
                  {includeDiscount && invoiceType !== "Bill" && (
                    <td className="td-color">
                      {i.DiscountRate || '-'}
                    </td>
                  )}
                  {!hideAccountAndTax &&
                    <td className="td-color">
                      {i.TaxAmount || '-'}
                    </td>
                  }
                  {trackingCategories.map((j) => (
                    <td key={j.TrackingCategoryID} className="td-color">
                      <TrackingSelect
                        lineItem={i}
                        trackingCategory={j}
                        dispatch={dispatch}
                        lineItemIndex={idx}
                        disabled
                      />
                    </td>
                  ))}
                  <td className="td-color text-right">
                    {formatCurrency(i.LineAmount || "-")}
                  </td>
                </Fieldset>
              );
            })}
          </tbody>
        </Table>
        {differentCurrencyShow() ? (
          <div className="text-center text-muted font-italic">
            <p>The Exchange rate may vary as it is calculated on the date of {invoiceType || "invoice"} generation. If required, it can be adjusted manually.</p>
          </div>
        ) : null}
      </div>
      {differentCurrencyShow() ? (
        <div className="text-right text-muted">
          <span style={{ fontSize: "1.2em" }}>
            <p>1 <CurrencySymbol currency={organisation.BaseCurrency}/> = {item.CurrencyRate}{" "}<CurrencySymbol currency={item.CurrencyCode}/></p>
            {/*<p>1 {item.CurrencyCode} = {parseFloat(reverseCalculate).toFixed(5)}{" "}{organisation.BaseCurrency}</p>*/}
          </span>
        </div>
      ) : null}
      <div className="d-flex align-items-start line-items-footer-responsive">
        <div className="mx-auto" />
        <table className="mb-3 border border-white" style={{minWidth:"30%"}}>
          <tbody className="text-right">
            <tr>
              <td className="border p-1">
                <Trans>Subtotal</Trans>
                {Number(item.TotalDiscount) > 0 ? (
                  <>
                    {" "}
                    <span className="text-muted tw-text-sm">
                      <Trans>
                        (includes a discount of{" "}
                        {{ amount: formatCurrency(item.TotalDiscount) }})
                      </Trans>
                    </span>
                  </>
                ) : null}
              </td>
              <td className="border p-1">{formatCurrency(item.SubTotal)}</td>
              <td className="bg-white border-0"></td>
            </tr>
            {item.LineAmountTypes === "Inclusive" && (
              <tr>
                <td className="border p-1">
                  <Trans>Includes Tax</Trans>
                </td>
                <td className="border p-1">{formatCurrency(item.TotalTax)}</td>
                <td className="bg-white border-0"></td>
              </tr>
            )}
            {item.LineAmountTypes === "Exclusive" && (
              <React.Fragment>
                <tr>
                  <td className="border p-1">
                    <Trans>Total Tax</Trans>
                  </td>
                  <td className="border p-1">{formatCurrency(item.TotalTax)}</td>
                  <td className="bg-white border-0"></td>
                </tr>
              </React.Fragment>
            )}
            <tr className="">
              <td className="h4 py-1 border p-1">
                <Trans>Total</Trans>
              </td>
              <td className="h4 border p-1">
                {formatCurrency(item.Total)}
              </td>
              <td className="bg-white border-0"></td>
            </tr>
            {!employee && !!item.Payments &&
              item.Payments.map((i) => (
                <tr key={i.PaymentID} className="">
                  <td className="border p-1">
                    <Link to={`/payments/${i.PaymentID}`}>
                      <Trans>{paymentText}</Trans>
                    </Link>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td className="border p-1">{formatCurrency(i.Amount)}</td>
                  <td className="bg-white border-0"></td>
                </tr>
              ))}
            {!employee && !!item.Prepayments &&
              item.Prepayments.map((i, index) => (
                <tr key={index} className="">
                  <td className="border p-1">
                    <Trans>Less</Trans>{": "}
                    <Link to={`/transactions/${item.Type === 'ACCREC' ? 'receive' : 'spend'}/prepayments/${i.PrepaymentID}`} >
                      <Trans>Prepayments</Trans> ({moment(i.Date).format("YYYY-MM-DD")})
                    </Link>
                  </td>
                  <td className="border p-1">{formatCurrency(i.AppliedAmount)}</td>
                  <td className="bg-white border-0"></td>
                </tr>
              ))}
            {!employee && !!item.Overpayments &&
              item.Overpayments.map((i) => (
                <tr key={i.OverpaymentID} className="">
                  <td className="border p-1">
                    <Trans>
                      Less{" "}
                      <Link
                        to={`/overpayments/${i.OverpaymentID}`}
                      >
                        Overpayment
                      </Link>
                    </Trans>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td className="border p-1">{formatCurrency(i.AppliedAmount)}</td>
                  <td className="bg-white border-0"></td>
                </tr>
              ))}
            {!employee && !!item.CreditNotes &&
              item.CreditNotes.map((i, idx) => (
                <tr key={idx} className="">
                  <td className="border p-1">
                    <Trans>
                      Less{" "}
                      <Link
                        to={`/${isPayable?"purchases":"accounts_receivable"}/credit_notes/view/${i.CreditNoteID}`}
                      >
                        Credit Note {{ CreditNoteNumber: i.CreditNoteNumber }}
                      </Link>
                    </Trans>
                    <br />
                    {moment(i.Date).format("YYYY-MM-DD")}
                  </td>
                  <td className="border p-1">{formatCurrency(i.AppliedAmount)}</td>
                  <td className="bg-white border-0"></td>
                </tr>
              ))}
            {!employee && !!item.Allocations &&
              item.Allocations.map((i, idx) => (
                <tr key={idx} className="">
                  <td className="border p-1">
                    <div className="">
                      <Trans>
                        Less Credit
                        {!!i.Invoice && (
                          <span>
                            {" "}
                            to{" "}
                            <Link
                              to={`/${isPayable?"purchases":"accounts_receivable"}/invoices/view/${i.Invoice.InvoiceID}`}
                            >
                              {{ InvoiceNumber: i.Invoice.InvoiceNumber }}
                            </Link>
                          </span>
                        )}
                      </Trans>
                      <br />
                      <strong>{moment(i.Date).format("YYYY-MM-DD")}</strong>
                    </div>
                  </td>
                  <td className="border p-1">
                    <div className="row w-100">
                      <div className="col-10 text-center">
                        {formatCurrency(i.Amount)}
                      </div>
                      <div className="col-2 p-0 text-right">
                        <Button color="link" onClick={() => removeAllocation(i)} className="link border px-1 py-0 text-danger m-0 bg-transparent">
                          <i className="fa fa-times" aria-hidden="true"></i>
                        </Button>
                      </div>
                    </div>
                  </td>
                  <td className="">

                  </td>
                </tr>
              ))}
            {!isUndefined(item.AmountDue) && (
              <tr className="">
                <td className="h4 py-1 border p-1">
                  <Trans>Amount {differentCurrencyShow() ? `${item.CurrencyCode}` : "Due"}</Trans>
                </td>
                <td className="h4 border p-1">{formatCurrency(item.AmountDue)}</td>
                <td className="bg-white border-0"></td>
              </tr>
            )}
            {!isUndefined(item.RemainingCredit) && ['ACCREC', 'ACCPAY'].includes(item?.Type) && item.RemainingCredit > 0 && (
              <tr className="">
                <td className="py-1 border p-1">
                  {formatCurrency(item.RemainingCredit)} <Trans>Remaining Credit</Trans>
                </td>
                <td className="h4 border p-1">
                  <Button color="link" onClick={PrepaymentModalToggle} className="link border p-1 bg-white"><Trans>Apply Credit</Trans></Button>
                </td>
                <td className="bg-white border-0"></td>
              </tr>
            )}
            {!isUndefined(item.RemainingCredit) && ['RECEIVE-OVERPAYMENT', 'RECEIVE-PREPAYMENT'].includes(item.Type) && item.RemainingCredit > 0 && (
              <tr className="">
                <td className="h4 py-1 border p-1">
                  <Trans>Remaining Credit</Trans><span><Link to={`${window.location.pathname}/allocate`}> (<Trans>Allocate</Trans>)</Link></span>
                </td>
                <td className="h4 border p-1">{formatCurrency(item.RemainingCredit)}</td>
                <td className="bg-white border-0"></td>
              </tr>
            )}
            {differentCurrencyShow() ? (
              <tr className="">
                <td className="h5 py-1 border p-1">
                  Total in <CurrencySymbol currency={organisation.BaseCurrency}/> 
                </td>
                <td className="h5 border p-1">{formatCurrency(total)}</td>
                <td className="bg-white border-0"></td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <PrepaymentModal isOpen={PrepaymentModalOpen} toggle={PrepaymentModalToggle} size={'lg'}>
        <ModalHeader toggle={PrepaymentModalToggle}><Trans>Apply credit for</Trans> {item?.Contact?.Name} </ModalHeader>
        <ModalBody style={{ overflowY: 'visible' }}>
          <LocalForm onSubmit={applyPrepayments}>
            <Table>
              <thead>
                <tr>
                  <th><Trans>Credit</Trans></th>
                  <th><Trans>Date</Trans></th>
                  <th><Trans>Reference</Trans></th>
                  <th><Trans>Outstanding credit	</Trans></th>
                  <th><Trans>To apply</Trans></th>
                </tr>
              </thead>
              <tbody>
                {prepayments?.map((prepayment, index) => (
                  <tr key={prepayment?.PrepaymentID}>
                    <td>{prepayment?.Reference}</td>
                    <td>{formatDate(item?.Date)}</td>
                    <td>{""}</td>
                    <td>{prepayment?.RemainingCredit}</td>
                    <td>
                      <FormGroup_ model={`.${index}.AppliedAmount`}>
                        <Control.text type="number" step="any" model={`.${index}.AppliedAmount`} />
                      </FormGroup_>
                      <FormGroup_ model={`.${index}.PrepaymentID`}>
                        <Control.text type="hidden" model={`.${index}.PrepaymentID`} defaultValue={prepayment?.PrepaymentID} />
                      </FormGroup_>
                    </td>
                  </tr>))
                }
              </tbody>
            </Table>
            <div className="actions d-flex justify-content-end">
              <SubmitButton color="success" className="btn-md">
                Confirm
              </SubmitButton>
              <Button color="secondary" onClick={PrepaymentModalToggle}>Cancel</Button>
            </div>
          </LocalForm>
        </ModalBody>
      </PrepaymentModal>
    </div>
  );
};

export const withLineItems = compose(
  withOrganisationContextConsumer,
  withTranslation(),
  withStateHandlers(
    ({ t }) => ({
      lineAmountTypes: [
        {
          label: t("Tax Inclusive"),
          value: "Inclusive",
        },
        {
          label: t("Tax Exclusive"),
          value: "Exclusive",
        },
        {
          label: t("No Tax"),
          value: "NoTax",
        },
      ],
    }),
    {
      setKeyedAccounts: () => (keyedAccounts) => ({ keyedAccounts }),
    }
  ),
  onMount(
    ({
      item,
      setKeyedAccounts,
      xero,
      organisation,
      fetchCurrencies,
      fetchOrganisation,
      fetchAccounts,
      fetchTaxRates,
      fetchItems,
    }) =>
      () => {
        const isReceivable =
          item.Type === "ACCREC" ||
          item.Type === "ACCRECCREDIT" ||
          item.Type === "RECEIVE" ||
          item.QuoteNumber !== undefined;
        const isPayable =
          item.Type === "PURCHASEORDER" ||
          item.Type === "ACCPAY" ||
          item.Type === "ACCPAYCREDIT" ||
          item.Type === "SPEND";

        const inventory = fetchItems()
          .filter((i) =>
            isReceivable ? i.IsSold : isPayable ? i.IsPurchased : false
          )
          .map((i) =>
            set(i, "Key", compact([i.Code, i.Name]).join(": "))
          );

        return Promise.props({
          isReceivable,
          isPayable,
          currencies: fetchCurrencies(),
          inventory,
          indexedInventory: inventory.then((i) => keyBy(i, "Code")),
          organisation: fetchOrganisation(),
          accounts: fetchAccounts().then((accounts) => {
            const classes = [
              "REVENUE",
              "EQUITY",
              "LIABILITY",
              "EXPENSE",
              "ASSET",
            ];
            setKeyedAccounts(keyBy(accounts, "Code"));

            return chain(accounts)
              .filter((i) => {
                if (i.AllowPayable !== undefined && isPayable) {
                  return i.AllowPayable;
                }
                if (i.AllowReceivable !== undefined && isReceivable) {
                  return i.AllowReceivable;
                }
                return includes(classes, i.Class) && i.Code;
              })
              .map((i) => ({
                ...i,
                Description: `${i.Code} ${i.Name}`,
              }))
              .groupBy("Class")
              .map((options, label) => ({
                Description: capitalize(label),
                options,
              }))
              .value();
          }),
          taxRates: fetchTaxRates(),
        });
      }
  ),
  withPopover(),
  withPropsOnChange(
    (props, nextProps) => { recalculate(props.item, nextProps) },
    () => { }
  ),
  withPropsOnChange(["dispatch"], (props) => recalculate(null, props)),
  withHandlers({
    handleAddLines:
      ({ dispatch, item, isReceivable, isPayable, includeDiscount }) =>
        (lines) => {
          const customerDiscount = get(item, "Contact.Discount") || undefined;
          const customerTaxType =
            get(
              item,
              isReceivable
                ? "Contact.AccountsReceivableTaxType"
                : "Contact.AccountsPayableTaxType"
            ) || undefined;
          const customerAccountCode =
            get(
              item,
              isReceivable
                ? "Contact.SalesDefaultAccountCode"
                : isPayable
                  ? "Contact.PurchasesDefaultAccountCode"
                  : null
            ) || undefined;

          range(lines).map((i) =>
            dispatch(
              actions.push("local.LineItems", {
                _id: shortid(),
                DiscountRate: includeDiscount ? customerDiscount : undefined,
                TaxType: customerTaxType,
                AccountCode: customerAccountCode,
              })
            )
          );
        },
  })
);

export default withLineItems(LineItems);